import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import ImageUploading, { ImageListType } from 'react-images-uploading';
import { getImageSize } from 'utils/general';

import { NextImage, Typography } from '@/shared/components/atoms';
import ErrorMessage from '@/shared/components/atoms/ErrorMessage';

import Cancel from '~/images/shared/cancel.svg';
import Gallery from '~/images/shared/GalleryOutline.svg';

export const ImagePickerNewD: React.FC<{ id: string; fullWidth?: boolean }> = ({
  id,
  fullWidth,
}) => {
  const { t } = useTranslation();
  const [imageList, setImageList] = useState<ImageListType>([]); // Control image list manually

  const {
    formState: { errors },
    setValue,
    control,
    watch,
  } = useFormContext();
  const imageWatcher = watch(id);

  useEffect(() => {
    const initialValue = control._defaultValues?.[id] || [];
    setImageList(initialValue);

    return () => {
      setImageList([]);
    };
  }, [id, control]);

  useEffect(() => {
    if (!imageWatcher) {
      setImageList([]);
    }
  }, [imageWatcher]);

  // TODO: khalid - handle multiple images selection

  return (
    <div>
      <Controller
        name={id}
        control={control}
        render={({ field: { onChange, value } }) => (
          <ImageUploading
            value={imageList} // Use controlled state
            onChange={(newImageList) => {
              setImageList(newImageList); // Update local state
              setValue(id, newImageList.length ? newImageList : undefined);
              onChange(newImageList.length ? newImageList : undefined);
            }}
          >
            {({ onImageUpload, onImageRemoveAll, dragProps }) => (
              <div
                className={`relative flex h-137 overflow-hidden ${
                  fullWidth ? 'w-full' : 'w-[373px]'
                } items-center justify-center rounded-xl border-sm border-dashed border-outline-default `}
                {...dragProps}
              >
                {imageList.length > 0 ? (
                  <div className='flex h-full w-full items-center justify-between '>
                    <div className='flex items-center gap-16 pl-16'>
                      <span
                        onClick={() => {
                          onImageRemoveAll(); // Remove images
                          setImageList([]); // Force update
                          setValue(id, undefined);
                          onChange(undefined);
                        }}
                        className='cursor-pointer'
                      >
                        <Cancel width='24' height='24' />
                      </span>
                      <div className='flex w-[80%] flex-col break-all'>
                        <Typography variant='subtitle'>
                          {imageList[0]?.file?.name}
                        </Typography>
                        <Typography variant='body2' weight='regular'>
                          {imageList[0]?.file?.size && (
                            <span>{getImageSize(imageList[0].file?.size)}</span>
                          )}
                        </Typography>
                      </div>
                    </div>
                    <NextImage
                      src={imageList[0]?.dataURL ?? value}
                      width={212}
                      height={312}
                      alt={id}
                    />
                  </div>
                ) : (
                  <div className='flex flex-col items-center justify-center'>
                    <div className='rounded-full bg-outline-default p-8'>
                      <Gallery width='18' height='18' />
                    </div>
                    <Typography
                      variant='subtitle2'
                      className='mt-[10px] text-center'
                    >
                      <span
                        onClick={onImageUpload}
                        className='cursor-pointer text-primary-60'
                      >
                        {t('Click to upload')}
                      </span>
                      <span className='text-surface-10'>
                        {` ${t('or')} ${t('drag and drop')}`}
                      </span>
                      <span className='text-surface-10'>
                        {t('image-format-size', {
                          px: 600,
                        })}
                      </span>
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </ImageUploading>
        )}
      />
      <ErrorMessage errorMessage={(errors?.[id]?.message || '') as string} />
    </div>
  );
};
